/** Personal Preferences as a modal over whatever the user was doing: tabs on the left of a centred dialog on desktop, a full-height list-then-pane sheet on phones. Workspace and admin settings stay full pages. The native shells keep their Preferences page, so this renders nothing there. */
import { useEffect, useRef } from 'react';
import {
  Bell, ChevronLeft, ChevronRight, Clock, LayoutList, Lock, MessageSquare, Settings, Wrench, XIcon,
  type LucideIcon,
} from '@/shared/ui/icons';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../shared/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../../shared/ui/sheet';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { cn } from '../../shared/ui/utils';
import type { User } from '../../types/auth';
import {PreferenceTabPanes} from './PreferenceTabPanes';
import {SettingsSaveStatus} from './SettingsSaveStatus';
import {requestSettingsNavigation} from '../../platform/navigation/settingsNavigationGuard';
import { SETTINGS_TABS, getSettingsTab, type SettingsTabId } from './settingsTabs';
import {
  closeSettingsModal,
  openSettingsModal,
  parseSettingsHash,
  selectSettingsTab,
  showSettingsTabList,
  usesSettingsModal,
  useSettingsModal,
} from './settingsModalStore';

const TAB_ICONS: Record<SettingsTabId, LucideIcon> = {
  general: Settings,
  notifications: Bell,
  availability: Clock,
  messages: MessageSquare,
  navigation: LayoutList,
  privacy: Lock,
  advanced: Wrench,
};

const DESCRIPTION = 'Your personal settings. They follow you across every workspace.';

async function navigatePreferences(action: () => void) {
  if (await requestSettingsNavigation()) action();
}

function DesktopSettings({ open, tab, user }: { open: boolean; tab: SettingsTabId; user: User }) {
  const paneRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // A new tab starts at its top, not wherever the last one was scrolled to.
  useEffect(() => { paneRef.current?.scrollTo({ top: 0 }); }, [tab]);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) void navigatePreferences(closeSettingsModal); }}>
      {/* Solid, not frosted: a pane of controls over a blurred page reads as greyed out. */}
      <DialogContent
        hideClose
        ref={contentRef}
        data-vibrant-fill="off"
        onOpenAutoFocus={(event) => {
          // Start on the open tab rather than the close button, so Tab moves on from where the user is.
          const current = contentRef.current?.querySelector<HTMLElement>('nav [aria-current="page"]');
          if (!current) return;
          event.preventDefault();
          current.focus({ preventScroll: true });
        }}
        className="h-[min(680px,calc(100dvh-4rem))] w-[min(880px,calc(100vw-4rem))] grid-cols-[220px_minmax(0,1fr)] gap-0 overflow-hidden p-0 sm:max-w-[880px]"
      >
        <nav aria-label="Preferences tabs" className="flex min-h-0 flex-col gap-1 overflow-y-auto bg-muted/40 px-2 pb-3 pt-2">
          <div className="mb-2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => {void navigatePreferences(closeSettingsModal);}}
              className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <XIcon className="size-4" />
              <span className="sr-only">Close</span>
            </button>
            <DialogTitle className="text-sm font-semibold">Preferences</DialogTitle>
          </div>
          <DialogDescription className="sr-only">{DESCRIPTION}</DialogDescription>
          {SETTINGS_TABS.map(({ id, label }) => {
            const Icon = TAB_ICONS[id];
            const active = id === tab;
            return (
              <button
                key={id}
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => {void navigatePreferences(() => selectSettingsTab(id));}}
                className={cn(
                  'flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-sm transition-colors',
                  active ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                <Icon className="size-4 shrink-0" />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </nav>
        <div ref={paneRef} className="min-h-0 overflow-y-auto px-7 pb-8 pt-5">
          <h2 className="mb-5 text-base font-semibold text-foreground">{getSettingsTab(tab).label}</h2>
          <SettingsSaveStatus/><PreferenceTabPanes tab={tab} user={user} singleColumn />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PhoneSettings({ open, tab, drilledIn, user }: { open: boolean; tab: SettingsTabId; drilledIn: boolean; user: User }) {
  const title = drilledIn ? getSettingsTab(tab).label : 'Preferences';
  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) void navigatePreferences(closeSettingsModal); }}>
      <SheetContent
        side="bottom"
        hideCloseButton
        className="z-[230] h-[100dvh] max-h-[100dvh] gap-0 rounded-none border-0 p-0 pt-[env(safe-area-inset-top)]"
      >
        <div className="grid h-14 shrink-0 grid-cols-[56px_1fr_56px] items-center">
          <button
            type="button"
            onClick={() => {void navigatePreferences(drilledIn ? showSettingsTabList : closeSettingsModal);}}
            className="flex size-11 items-center justify-center justify-self-center rounded-full text-foreground active:bg-muted/60"
          >
            {drilledIn ? <ChevronLeft className="size-6" /> : <XIcon className="size-5" />}
            <span className="sr-only">{drilledIn ? 'Back to Preferences' : 'Close'}</span>
          </button>
          <SheetTitle className="truncate text-center text-[17px] font-semibold">{title}</SheetTitle>
        </div>
        <SheetDescription className="sr-only">{DESCRIPTION}</SheetDescription>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+24px)]">
          {drilledIn ? (
            <div className="px-4 py-3">
              <SettingsSaveStatus/><PreferenceTabPanes tab={tab} user={user} singleColumn />
            </div>
          ) : (
            <nav aria-label="Preferences tabs" className="px-3 py-2">
              {SETTINGS_TABS.map(({ id, label }, index) => {
                const Icon = TAB_ICONS[id];
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {void navigatePreferences(() => selectSettingsTab(id));}}
                    className={cn('flex w-full items-center gap-3 px-3 py-4 text-left transition-colors active:bg-muted/60', index > 0 && 'border-t border-border/45')}
                  >
                    <Icon className="size-5 shrink-0 text-foreground" />
                    <span className="min-w-0 flex-1 truncate text-[15px] text-foreground">{label}</span>
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground/60" />
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function SettingsModal({ user }: { user: User }) {
  const { open, tab, drilledIn } = useSettingsModal();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const enabled = usesSettingsModal();

  // An address that already names a tab (a reload, a shared link) opens on it.
  useEffect(() => {
    if (!enabled) return;
    const fromHash = parseSettingsHash(window.location.hash);
    if (fromHash !== undefined) openSettingsModal(fromHash);
  }, [enabled]);

  // Going somewhere else, from a link inside a pane or a command, leaves the modal behind.
  useEffect(() => {
    if (!enabled) return;
    const onNavigate = (event: Event) => {
      if ((event as CustomEvent<{ view?: string }>).detail?.view !== 'preferences') closeSettingsModal();
    };
    // Back to an entry that does not name a tab closes it; an old /preferences entry reopens it through the address parser.
    const onPopState = () => { if (parseSettingsHash(window.location.hash) === undefined) closeSettingsModal(); };
    // A same-page link such as href="#preferences/privacy" opens that tab.
    const onHashChange = () => {
      const fromHash = parseSettingsHash(window.location.hash);
      if (fromHash !== undefined) openSettingsModal(fromHash);
    };
    window.addEventListener('app:navigate', onNavigate);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('app:navigate', onNavigate);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [enabled]);

  if (!enabled) return null;
  return isDesktop
    ? <DesktopSettings open={open} tab={tab} user={user} />
    : <PhoneSettings open={open} tab={tab} drilledIn={drilledIn} user={user} />;
}
